<template>
  <div>
    <Nav></Nav>
    <b-container fluid="xl">
      <b-row class="mt-3">
        <b-col>
          <h1>Configuración → Funcionalidades</h1>
          <p class="text-muted">
            Activa, desactiva y parametriza cada funcionalidad por compañía. Un flag apagado
            oculta la función y sus endpoints devuelven <code>feature_disabled</code>.
          </p>
        </b-col>
      </b-row>

      <b-row v-if="loading">
        <b-col class="text-center my-4">
          <b-spinner></b-spinner>
        </b-col>
      </b-row>

      <div v-for="section in sections" :key="section" v-else>
        <h4 class="mt-4">{{ section }}</h4>
        <b-table small striped hover responsive :items="rowsForSection(section)" :fields="fields">
          <template #cell(enabled)="data">
            <b-form-checkbox
              switch
              :checked="data.item.enabled"
              @change="toggle(data.item, $event)">
            </b-form-checkbox>
          </template>
          <template #cell(requires)="data">
            <b-badge v-for="req in data.item.requires" :key="req" variant="secondary" class="mr-1">
              {{ req }}
            </b-badge>
            <span v-if="!data.item.requires || !data.item.requires.length">—</span>
          </template>
          <template #cell(reserved)="data">
            <b-badge v-if="data.item.reserved" variant="warning">reservado</b-badge>
          </template>
          <template #cell(buttons)="data">
            <b-button size="sm" variant="warning" @click="openEditor(data.item)">Configurar</b-button>
          </template>
        </b-table>
      </div>
    </b-container>

    <!-- Editor de config_json -->
    <b-modal v-model="showEditor" :title="'Configurar: ' + (editing ? editing.key : '')" size="lg" @ok="saveConfig" ok-title="Guardar">
      <div v-if="editing && editing.key === 'loyalty.points'" class="mb-3">
        <b-form-group label="Producto de redención de puntos" description="Debe ser un producto de descuento con «No genera puntos» activo.">
          <b-form-select v-model="editConfig.redemption_product_id" :options="productOptions" @change="validateRedemptionProduct"></b-form-select>
        </b-form-group>
      </div>

      <!-- Parámetros del dashboard ejecutivo (doc 08 A.15). -->
      <div v-if="editing && editing.key === 'reports.executive'" class="mb-3">
        <b-row>
          <b-col md="6">
            <b-form-group label="Ticket de referencia" description="Para comparativos de ticket promedio.">
              <b-form-input type="number" v-model.number="editConfig.avg_ticket_reference"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Margen de contribución" description="0–1 (p. ej. 0.35 = 35%).">
              <b-form-input type="number" step="0.01" v-model.number="editConfig.contribution_margin"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Fuente de metas">
              <b-form-select v-model="editConfig.goal_source" :options="goalSourceOptions"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Materialización nocturna" description="Recalcula tablas analíticas cada noche.">
              <b-form-checkbox switch v-model="editConfig.materialize_daily">
                {{ editConfig.materialize_daily ? 'activada' : 'desactivada' }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Alerta · caída de ventas (%)" description="Umbral negativo, p. ej. -10.">
              <b-form-input type="number" :value="alertRule('sales_drop_pct')"
                @input="setAlertRule('sales_drop_pct', $event)"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Alerta · SLA de entrega (min)">
              <b-form-input type="number" :value="alertRule('sla_delivery_minutes')"
                @input="setAlertRule('sla_delivery_minutes', $event)"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-button size="sm" variant="outline-secondary" @click="recalcRange" :disabled="recalculating">
          <b-spinner v-if="recalculating" small></b-spinner>
          Recalcular rango ahora
        </b-button>
      </div>
      <b-tabs v-if="editing">
        <b-tab title="Formulario" active>
          <b-form-group v-for="f in editing.fields" :key="f.name" :label="f.name" :description="'default: ' + JSON.stringify(f.default)">
            <b-form-checkbox v-if="f.type === 'boolean'" v-model="editConfig[f.name]" switch>
              {{ editConfig[f.name] ? 'sí' : 'no' }}
            </b-form-checkbox>
            <b-form-input v-else-if="f.type === 'number'" type="number" v-model.number="editConfig[f.name]"></b-form-input>
            <b-form-input v-else-if="f.type === 'string'" v-model="editConfig[f.name]"></b-form-input>
            <b-form-textarea v-else rows="2" :value="stringifyField(editConfig[f.name])" @input="setJsonField(f.name, $event)"></b-form-textarea>
          </b-form-group>
        </b-tab>
        <b-tab title="JSON avanzado">
          <b-form-textarea v-model="rawJson" rows="12" style="font-family: monospace;"></b-form-textarea>
          <small v-if="rawJsonError" class="text-danger">{{ rawJsonError }}</small>
        </b-tab>
      </b-tabs>
    </b-modal>
  </div>
</template>

<script>
import Nav from "../../components/Nav.vue";
import featuresService from "../../services/featuresService";
import axios from "axios";

export default {
  name: "funcionalidades",
  components: { Nav },
  data() {
    return {
      loading: true,
      schema: [],
      features: {},
      fields: [
        { key: "key", label: "Llave" },
        { key: "description", label: "Descripción" },
        { key: "requires", label: "Requiere" },
        { key: "reserved", label: "" },
        { key: "enabled", label: "Activo" },
        { key: "buttons", label: "" },
      ],
      showEditor: false,
      editing: null,
      editConfig: {},
      rawJson: "",
      rawJsonError: "",
      activeTab: 0,
      products: [],
      recalculating: false,
      goalSourceOptions: [
        { value: "sales_projection", text: "Proyección de ventas" },
        { value: "sales_goals", text: "Metas capturadas (sales_goals)" },
        { value: "previous_year", text: "Año anterior" },
      ],
    };
  },
  computed: {
    sections() {
      const set = [];
      this.schema.forEach((k) => {
        if (!set.includes(k.section)) set.push(k.section);
      });
      return set;
    },
    productOptions() {
      return [{ value: null, text: "— selecciona —" }].concat(
        this.products.map((p) => ({ value: p.id, text: (p.clave ? p.clave + " · " : "") + (p.short_name || p.ndescription || ("#" + p.id)) }))
      );
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    // A.15: helpers para las reglas de alerta anidadas de reports.executive.
    alertRule(name) {
      const r = this.editConfig.alert_rules;
      return r ? r[name] : null;
    },
    setAlertRule(name, val) {
      if (!this.editConfig.alert_rules || typeof this.editConfig.alert_rules !== "object") {
        this.$set(this.editConfig, "alert_rules", {});
      }
      const n = Number(val);
      this.$set(this.editConfig.alert_rules, name, isNaN(n) ? val : n);
    },
    recalcRange() {
      this.recalculating = true;
      axios.post("/jobs/analytics_materialize")
        .then(() => this.makeToast("Listo", "Materialización ejecutada.", "success"))
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => { this.recalculating = false; });
    },
    load() {
      this.loading = true;
      Promise.all([featuresService.getSchema(), featuresService.getAll()])
        .then(([schemaRes, featRes]) => {
          this.schema = (schemaRes.data && schemaRes.data.schema) || [];
          this.features = (featRes.data && featRes.data.features) || {};
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => {
          this.loading = false;
        });
    },
    rowsForSection(section) {
      return this.schema
        .filter((k) => k.section === section)
        .map((k) => ({
          key: k.key,
          description: k.description,
          requires: k.requires,
          reserved: k.reserved,
          fields: k.fields,
          enabled: !!(this.features[k.key] && this.features[k.key].enabled),
          config: (this.features[k.key] && this.features[k.key].config) || {},
        }));
    },
    toggle(item, enabled) {
      featuresService
        .update(item.key, { enabled, config: item.config })
        .then(() => {
          this.features[item.key] = { enabled, config: item.config };
          this.makeToast("Guardado", item.key + (enabled ? " activado" : " desactivado"), "success");
        })
        .catch((err) => {
          this.makeToast("Error", this.errMsg(err), "danger");
          this.load(); // revierte el switch al estado real
        });
    },
    openEditor(item) {
      this.editing = item;
      this.editConfig = JSON.parse(JSON.stringify(item.config || {}));
      this.rawJson = JSON.stringify(this.editConfig, null, 2);
      this.rawJsonError = "";
      this.showEditor = true;
      if (item.key === "loyalty.points") this.loadProducts();
    },
    loadProducts() {
      if (this.products.length) return;
      axios.get("/products").then((res) => {
        this.products = res.data || [];
      }).catch(() => {});
    },
    validateRedemptionProduct(id) {
      if (!id) return;
      axios.get("/product/" + id).then((res) => {
        const p = res.data || {};
        if (!p.loyalty_excluded) {
          this.makeToast("Atención", "El producto seleccionado no tiene «No genera puntos» activo; actívalo en el catálogo.", "warning");
        }
      }).catch(() => {});
    },
    stringifyField(v) {
      return typeof v === "string" ? v : JSON.stringify(v);
    },
    setJsonField(name, val) {
      try {
        this.editConfig[name] = JSON.parse(val);
      } catch (e) {
        this.editConfig[name] = val;
      }
    },
    saveConfig(evt) {
      // Prefiere el JSON avanzado si es válido; si no, usa el formulario.
      let config = this.editConfig;
      if (this.rawJson && this.rawJson.trim()) {
        try {
          config = JSON.parse(this.rawJson);
        } catch (e) {
          this.rawJsonError = "JSON inválido: " + e.message;
          evt.preventDefault();
          return;
        }
      }
      featuresService
        .update(this.editing.key, { enabled: this.features[this.editing.key] ? this.features[this.editing.key].enabled : false, config })
        .then(() => {
          this.features[this.editing.key] = {
            enabled: this.features[this.editing.key] ? this.features[this.editing.key].enabled : false,
            config,
          };
          this.makeToast("Guardado", "Configuración actualizada", "success");
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
    },
    errMsg(err) {
      if (err && err.response && err.response.data) {
        return typeof err.response.data === "string" ? err.response.data : (err.response.data.error || JSON.stringify(err.response.data));
      }
      return err && err.message ? err.message : "Error desconocido";
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, { title, variant, autoHideDelay: 5000, appendToast: true });
    },
  },
};
</script>
